import { supabase } from './supabaseService';

export type IntegrationStatus = 'healthy' | 'degraded' | 'failed' | 'unknown';
export type EventSeverity = 'info' | 'warning' | 'error' | 'critical';
export type JobType = 'send_email' | 'analyze_lead' | 'sync_calendar' | 'backup' | 'other';
export type JobStatus = 'pending' | 'in_progress' | 'completed' | 'failed';

export const RETRYABLE_JOB_TYPES: JobType[] = ['send_email'];

export const DEGRADED_THRESHOLD_HOURS = 2;

export interface IntegrationHealth {
  service_name: string;
  status: IntegrationStatus;
  last_check_at: string | null;
  last_success_at: string | null;
  last_error_message: string | null;
  response_time_ms: number | null;
  meta: Record<string, unknown>;
  updated_at: string;
  effective_status?: IntegrationStatus;
}

export interface SystemEvent {
  id: string;
  event_type: string;
  source_module: string;
  severity: EventSeverity;
  message: string;
  meta: Record<string, unknown>;
  created_at: string;
}

export interface IntegrationJob {
  id: string;
  job_type: JobType;
  status: JobStatus;
  attempts: number;
  last_attempt_at: string | null;
  error_message: string | null;
  payload: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface JobsSummary {
  pending: number;
  in_progress: number;
  completed: number;
  failed: number;
}

function computeEffectiveStatus(row: IntegrationHealth): IntegrationStatus {
  if (row.status === 'failed' || row.status === 'unknown') return row.status;

  if (row.status === 'healthy' && (!row.last_success_at || !Number.isFinite(Date.parse(row.last_success_at)))) return 'unknown';
  if (row.status === 'healthy' && row.last_success_at) {
    const lastSuccess = new Date(row.last_success_at).getTime();
    const now = Date.now();
    const hoursElapsed = (now - lastSuccess) / (1000 * 60 * 60);
    if (hoursElapsed > DEGRADED_THRESHOLD_HOURS) return 'degraded';
  }

  if (row.status === 'degraded') return 'degraded';

  return row.status;
}

async function getIntegrationHealth(): Promise<IntegrationHealth[]> {
  const { data, error } = await supabase
    .from('integration_health')
    .select('*')
    .order('service_name');

  if (error) throw error;

  return (data || []).map((row) => ({
    ...row,
    effective_status: computeEffectiveStatus(row as IntegrationHealth),
  }));
}

async function updateIntegrationHealth(
  serviceName: string,
  status: IntegrationStatus,
  responseTimeMs?: number,
  errorMessage?: string
): Promise<void> {
  const now = new Date().toISOString();
  const payload: Record<string, unknown> = {
    service_name: serviceName,
    status,
    last_check_at: now,
    last_error_message: errorMessage ?? null,
    response_time_ms: responseTimeMs ?? null,
    updated_at: now,
  };

  if (status === 'healthy') {
    payload.last_success_at = now;
  }

  const { error } = await supabase
    .from('integration_health')
    .upsert(payload, { onConflict: 'service_name' });

  if (error) {
    throw error;
  }
}

async function recordSystemEvent(
  eventType: string,
  sourceModule: string,
  severity: EventSeverity,
  message: string,
  meta?: Record<string, unknown>
): Promise<void> {
  const { error } = await supabase.from('system_events').insert({
    event_type: eventType,
    source_module: sourceModule,
    severity,
    message,
    meta: meta ?? {},
  });

  if (error) {
    console.error('systemHealthService: failed to record system event', error);
  }
}

async function getRecentEvents(
  limit = 50,
  severityFilter?: EventSeverity
): Promise<SystemEvent[]> {
  let query = supabase
    .from('system_events')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit);

  if (severityFilter) {
    query = query.eq('severity', severityFilter);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

async function getIntegrationJobsSummary(): Promise<JobsSummary> {
  const statuses: JobStatus[] = ['pending', 'in_progress', 'completed', 'failed'];
  const counts = await Promise.all(statuses.map(async status => {
    const { count, error } = await supabase.from('integration_jobs').select('id', { count: 'exact', head: true }).eq('status', status);
    if (error) throw error;
    return count ?? 0;
  }));
  return { pending: counts[0], in_progress: counts[1], completed: counts[2], failed: counts[3] };
}

async function getIntegrationJobs(statusFilter?: JobStatus): Promise<IntegrationJob[]> {
  let query = supabase
    .from('integration_jobs')
    .select('*')
    .in('status', ['pending', 'in_progress', 'failed'])
    .order('created_at', { ascending: false })
    .limit(100);

  if (statusFilter) {
    query = query.eq('status', statusFilter);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

async function recordJob(
  jobType: JobType,
  payload: Record<string, unknown>,
  errorMessage?: string
): Promise<string | null> {
  const { data, error } = await supabase
    .from('integration_jobs')
    .insert({
      job_type: jobType,
      status: errorMessage ? 'failed' : 'pending',
      attempts: errorMessage ? 1 : 0,
      last_attempt_at: errorMessage ? new Date().toISOString() : null,
      error_message: errorMessage ?? null,
      payload,
    })
    .select('id')
    .maybeSingle();

  if (error) {
    console.error('systemHealthService: failed to record job', error);
    return null;
  }
  return data?.id ?? null;
}

async function retryJob(jobId: string): Promise<{ success: boolean; message: string }> {
  const { data: job, error } = await supabase
    .from('integration_jobs')
    .select('*')
    .eq('id', jobId)
    .maybeSingle();

  if (error || !job) {
    return { success: false, message: 'Tarea no encontrada' };
  }

  if (job.status !== 'failed') return { success: false, message: 'Esta tarea ya no está pendiente de reintento' };
  if (!RETRYABLE_JOB_TYPES.includes(job.job_type as JobType)) {
    return {
      success: false,
      message: `Las tareas de tipo "${job.job_type}" requieren revisión manual`,
    };
  }

  const claim = await supabase
    .from('integration_jobs')
    .update({
      status: 'in_progress',
      attempts: (job.attempts || 0) + 1,
      last_attempt_at: new Date().toISOString(),
      error_message: null,
      updated_at: new Date().toISOString(),
    })
    .eq('id', jobId).eq('status', 'failed').select('id');
  if (claim.error || !claim.data?.length) return { success: false, message: 'No se pudo iniciar el reintento o ya lo inició otra persona' };

  try {
    if (job.job_type === 'send_email') {
      const { resendService } = await import('./resendService');
      const p = job.payload as Record<string, unknown>;
      await resendService.sendEmail({
        to: p.to as string | string[],
        subject: p.subject as string,
        html: p.html as string,
        text: p.text as string | undefined,
      }, { recordFailureJob: false });
    }

    const saved = await supabase
      .from('integration_jobs')
      .update({ status: 'completed', updated_at: new Date().toISOString() })
      .eq('id', jobId).eq('status', 'in_progress').select('id');

    if (saved.error || !saved.data?.length) return { success: false, message: 'La acción terminó, pero no se pudo guardar su estado. Comprueba el resultado antes de repetirla.' };
    return { success: true, message: 'Tarea completada' };
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    await supabase
      .from('integration_jobs')
      .update({
        status: 'failed',
        error_message: msg,
        updated_at: new Date().toISOString(),
      })
      .eq('id', jobId);

    return { success: false, message: `Error al reintentar: ${msg}` };
  }
}

async function getCriticalEventCount24h(): Promise<number> {
  const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
  const { count, error } = await supabase
    .from('system_events')
    .select('*', { count: 'exact', head: true })
    .in('severity', ['error', 'critical'])
    .gte('created_at', since);

  if (error) throw error;
  return count ?? 0;
}

export const systemHealthService = {
  getIntegrationHealth,
  updateIntegrationHealth,
  recordSystemEvent,
  getRecentEvents,
  getIntegrationJobsSummary,
  getIntegrationJobs,
  recordJob,
  retryJob,
  getCriticalEventCount24h,
  computeEffectiveStatus,
};
